import { useMemo, useState } from 'react';
import type { ClearanceNote } from '../../../js/api/models/ClearanceNote';
import type { ConnectedFacilityNote } from '../../../js/api/models/ConnectedFacilityNote';
import type { NeedNote } from '../../../js/api/models/NeedNote';
import type { OpportunityNote } from '../../../js/api/models/OpportunityNote';
import type { PatchedClearanceNote } from '../../../js/api/models/PatchedClearanceNote';
import type { PatchedConnectedFacilityNote } from '../../../js/api/models/PatchedConnectedFacilityNote';
import type { PatchedNeedNote } from '../../../js/api/models/PatchedNeedNote';
import type { PatchedOpportunityNote } from '../../../js/api/models/PatchedOpportunityNote';
import type {
  ActivityNote,
  ActivityStatusHistory,
  ActivityHistory,
  ActivityEventHistory,
  UnifiedActivity,
} from '../utils/activity-types';
import { ActivityItem } from './activity-item';
import { Button } from './button';
import { Checkbox, CheckboxField } from './checkbox';
import { Label } from './fieldset';

export type NoteMutations = {
  updateNote: (
    noteId: string,
    data:
      | PatchedOpportunityNote
      | PatchedNeedNote
      | PatchedConnectedFacilityNote
      | PatchedClearanceNote
  ) => Promise<OpportunityNote | NeedNote | ConnectedFacilityNote | ClearanceNote>;
  deleteNote: (noteId: string) => Promise<void>;
  notesQueryKey: readonly unknown[];
  // Optional: additional query keys to invalidate after note edits/deletes (e.g., status history)
  extraInvalidateQueryKeys?: readonly (readonly unknown[])[];
};

type ActivityLogProps = {
  notes: ActivityNote[] | undefined;
  statusHistory: ActivityStatusHistory[] | undefined;
  changeHistory: ActivityHistory[] | undefined;
  eventHistory?: ActivityEventHistory[] | undefined;
  noteMutations?: NoteMutations;
  enableCommentsFilter?: boolean;
  enableStatusChangesFilter?: boolean;
  enableUpdatesFilter?: boolean;
  enableEventsFilter?: boolean;
};

/**
 * Compiles notes, status history, change history, and event history into a unified activity list
 */
function compileActivityList(
  notes: ActivityNote[] | undefined,
  statusHistory: ActivityStatusHistory[] | undefined,
  changeHistory: ActivityHistory[] | undefined,
  eventHistory: ActivityEventHistory[] | undefined
): UnifiedActivity[] {
  const activities: UnifiedActivity[] = [];

  if (notes && notes.length > 0) {
    notes.forEach((note) => {
      activities.push({
        id: note.id,
        activity_type: 'note',
        activity_datetime: note.note_at,
        activity_object: note,
        activity_icon: 'fas fa-comment-dots',
      });
    });
  }

  if (statusHistory && statusHistory.length > 0) {
    statusHistory.forEach((status) => {
      activities.push({
        id: status.id,
        activity_type: 'status',
        activity_datetime: status.change_at,
        activity_object: status,
        activity_icon: 'fas fa-sync-alt',
      });
    });
  }

  if (changeHistory && changeHistory.length > 0) {
    changeHistory.forEach((change) => {
      activities.push({
        id: change.id,
        activity_type: 'update',
        activity_datetime: change.change_at,
        activity_object: change,
        activity_icon: 'fas fa-user-pen',
      });
    });
  }

  if (eventHistory && eventHistory.length > 0) {
    eventHistory.forEach((event) => {
      activities.push({
        id: event.id,
        activity_type: 'event',
        activity_datetime: event.event_at,
        activity_object: event,
        activity_icon: 'fas fa-seal',
      });
    });
  }

  activities.sort((a, b) => {
    const dateA = new Date(a.activity_datetime).getTime();
    const dateB = new Date(b.activity_datetime).getTime();
    return dateB - dateA; // Descending order
  });

  return activities;
}

export function ActivityLog({
  notes,
  statusHistory,
  changeHistory,
  eventHistory,
  noteMutations,
  enableCommentsFilter = false,
  enableStatusChangesFilter = false,
  enableUpdatesFilter = false,
  enableEventsFilter = false,
}: ActivityLogProps) {
  const [commentsVisibleInList, setCommentsVisibleInList] = useState(enableCommentsFilter);
  const [statusChangesVisibleInList, setStatusChangesVisibleInList] =
    useState(enableStatusChangesFilter);
  const [updatesVisibleInList, setUpdatesVisibleInList] = useState(enableUpdatesFilter);
  const [eventsVisibleInList, setEventsVisibleInList] = useState(enableEventsFilter);
  const [sortDirection, setSortDirection] = useState<'newest' | 'oldest'>('newest');

  const activityList = useMemo(() => {
    return compileActivityList(notes, statusHistory, changeHistory, eventHistory);
  }, [notes, statusHistory, changeHistory, eventHistory]);

  const filteredAndSortedActivityList = useMemo(() => {
    const isVisible = (type: UnifiedActivity['activity_type']) => {
      const visibilityMap = {
        note: enableCommentsFilter ? commentsVisibleInList : false,
        status: enableStatusChangesFilter ? statusChangesVisibleInList : false,
        update: enableUpdatesFilter ? updatesVisibleInList : false,
        event: enableEventsFilter ? eventsVisibleInList : false,
      };
      return visibilityMap[type];
    };
    let filtered = activityList.filter((activity) => isVisible(activity.activity_type));

    filtered = [...filtered].sort((a, b) => {
      const dateA = new Date(a.activity_datetime).getTime();
      const dateB = new Date(b.activity_datetime).getTime();
      return sortDirection === 'newest' ? dateB - dateA : dateA - dateB;
    });

    return filtered;
  }, [
    activityList,
    enableCommentsFilter,
    enableStatusChangesFilter,
    enableUpdatesFilter,
    enableEventsFilter,
    commentsVisibleInList,
    statusChangesVisibleInList,
    updatesVisibleInList,
    eventsVisibleInList,
    sortDirection,
  ]);

  return (
    <>
      {/* Filter Bar */}
      <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-x-4 tw:gap-y-2 tw:mb-4 tw:mt-0 tw:p-2 tw:bg-zinc-50 tw:border-b tw:border-zinc-200">
        <div className="tw:flex tw:items-center tw:gap-2 tw:md:gap-3 flex-wrap">
          {enableCommentsFilter && (
            <CheckboxField
              className="tw:!gap-x-1 tw:md:!gap-x-2"
              data-testid="activity-log-filter-comments"
            >
              <Checkbox
                checked={commentsVisibleInList}
                onChange={(checked) => setCommentsVisibleInList(checked)}
                color="brandgreen1"
              />
              <Label>Comments</Label>
            </CheckboxField>
          )}
          {enableStatusChangesFilter && (
            <CheckboxField
              className="tw:!gap-x-1 tw:md:!gap-x-2"
              data-testid="activity-log-filter-status"
            >
              <Checkbox
                checked={statusChangesVisibleInList}
                onChange={(checked) => setStatusChangesVisibleInList(checked)}
                color="brandgreen1"
              />
              <Label>Status</Label>
            </CheckboxField>
          )}
          {enableUpdatesFilter && (
            <CheckboxField
              className="tw:!gap-x-1 tw:md:!gap-x-2"
              data-testid="activity-log-filter-updates"
            >
              <Checkbox
                checked={updatesVisibleInList}
                onChange={(checked) => setUpdatesVisibleInList(checked)}
                color="brandgreen1"
              />
              <Label>Updates</Label>
            </CheckboxField>
          )}
          {enableEventsFilter && (
            <CheckboxField
              className="tw:!gap-x-1 tw:md:!gap-x-2"
              data-testid="activity-log-filter-events"
            >
              <Checkbox
                checked={eventsVisibleInList}
                onChange={(checked) => setEventsVisibleInList(checked)}
                color="brandgreen1"
              />
              <Label>Events</Label>
            </CheckboxField>
          )}
        </div>
        <Button
          color="brand-green-outline"
          small
          onClick={() => setSortDirection(sortDirection === 'newest' ? 'oldest' : 'newest')}
          data-testid="activity-log-sort"
        >
          {sortDirection === 'newest' ? 'Newest First' : 'Oldest First'}
        </Button>
      </div>
      {/* Activity List */}
      <div>
        {filteredAndSortedActivityList.map((activity) => (
          <div key={activity.id} data-testid="activity-log-item">
            <ActivityItem activity={activity} noteMutations={noteMutations} />
          </div>
        ))}
      </div>
    </>
  );
}
